import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { XCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { certificationService } from '@/services/certification.service';
import CertificationDetailsView from '@/components/certifications/CertificationDetailsView';

export default function CertificationDetails() {
  const { id } = useParams<{ id: string }>();

  // Fetch certification details
  const { data: certification, isLoading, error } = useQuery({
    queryKey: ['certification-details', id],
    queryFn: () => certificationService.getCertificationDetails(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {/* Header Skeleton */}
          <div className="flex items-center gap-3">
            <SkeletonLoader type="custom" width="40px" height="40px" className="rounded-lg" />
            <SkeletonLoader type="title" className="h-6 w-48" />
          </div>

          {/* Banner Skeleton */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <SkeletonLoader type="custom" width="100%" height="200px" />
            <div className="p-6">
              <SkeletonLoader type="title" className="h-8 w-3/4 mb-3" />
              <SkeletonLoader type="text" className="h-4 w-full mb-2" />
              <SkeletonLoader type="text" className="h-4 w-2/3" />
            </div>
          </div>

          {/* Stats Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                <SkeletonLoader type="text" className="h-10 w-16 mb-2" />
                <SkeletonLoader type="text" className="h-4 w-24" />
              </div>
            ))}
          </div>

          {/* Questions Skeleton */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <SkeletonLoader type="title" className="w-48" />
              <SkeletonLoader type="custom" width="150px" height="40px" className="rounded-lg" />
            </div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-6">
                  <SkeletonLoader type="text" className="h-6 w-3/4 mb-4" />
                  <div className="space-y-3">
                    <SkeletonLoader type="text" className="h-12 w-full rounded-lg" />
                    <SkeletonLoader type="text" className="h-12 w-full rounded-lg" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !certification) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Certification Not Found</h2>
          <p className="text-gray-600 mb-6">The certification you're looking for doesn't exist or has been removed.</p>
          <Link to="/admin/certifications" className="btn btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Certifications
          </Link>
        </div>
      </div>
    );
  }

  return <CertificationDetailsView certification={certification} certificationId={id!} />;
}
